/**
 * Vista a calendario mensile degli allenamenti (V10).
 *
 * La griglia comune (`CalendarioMese`) sui mesi della stagione: su telefono
 * un pallino per allenamento, colorato per stato; da tablet in su l'ora in
 * una pastiglia dello stesso colore. Toccando un giorno se ne leggono le
 * righe, e chi gestisce crea un allenamento con quel giorno gia compilato.
 * Mese e giorno scelti stanno nell'URL, come la vista.
 */

import { PlusIcon } from 'lucide-react';
import { dataLocale, oraLocale } from '@calcio/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CalendarioMese } from '../comuni/CalendarioMese.js';
import {
  type GiornoProposto,
  mesiDellaStagione,
  nellaStagione,
  oraPiuUsata,
  perGiorno,
} from '../comuni/calendario.js';
import {
  type AllenamentoInElenco,
  COLORE_SEGNO,
  ETICHETTA_SEGNO,
  PALLINO_SEGNO,
  type Segno,
  segnoDi,
} from './allenamenti.js';
import { RigaAllenamento } from './RigaAllenamento.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const LEGENDA: Segno[] = ['programmato', 'svolto', 'annullato', 'daChiudere'];

export function CalendarioAllenamenti({
  ss,
  stagione,
  tutti,
  filtrati,
  mese,
  giorno,
  gestisce,
  adesso,
  onMese,
  onGiorno,
  onNuovo,
}: {
  ss: string;
  stagione: { dataInizio: string; dataFine: string };
  /** Tutti gli allenamenti, per l'ora da proporre. */
  tutti: readonly AllenamentoInElenco[];
  /** Quelli che passano il filtro per stato: gli unici mostrati. */
  filtrati: readonly AllenamentoInElenco[];
  mese: string;
  giorno: string | null;
  gestisce: boolean;
  adesso: Date;
  onMese: (mese: string) => void;
  onGiorno: (giorno: string) => void;
  /** Apre il nuovo allenamento con il giorno scelto e l'ora piu usata. */
  onNuovo: (giorno: GiornoProposto) => void;
}) {
  const oggi = dataLocale(adesso);
  return (
    <CalendarioMese
      mesi={mesiDellaStagione(stagione)}
      mese={mese}
      giorno={giorno}
      oggi={oggi}
      giorni={perGiorno(filtrati)}
      pallino={(a) => PALLINO_SEGNO[segnoDi(a, oggi)]}
      pastiglia={(a) => ({
        testo: oraLocale(a.dataOra),
        className: cn(COLORE_SEGNO[segnoDi(a, oggi)], a.stato === 'annullato' && 'line-through'),
      })}
      legenda={LEGENDA.map((s) => ({ etichetta: ETICHETTA_SEGNO[s], colore: PALLINO_SEGNO[s] }))}
      conteggio={(n) =>
        n === 0 ? 'nessun allenamento' : n === 1 ? '1 allenamento' : `${n} allenamenti`
      }
      invito="Tocca un giorno per vederne gli allenamenti."
      onMese={onMese}
      onGiorno={onGiorno}
      dettaglio={(scelto, delGiorno) => ({
        nota:
          delGiorno.some((a) => segnoDi(a, oggi) === 'daChiudere') &&
          'Programmato con la data già passata: segnalo svolto facendo l’appello, o annullalo.',
        contenuto: (
          <>
            {delGiorno.length === 0 ? (
              <StatoVuoto titolo="Nessun allenamento" />
            ) : (
              <ul className="flex flex-col gap-2">
                {delGiorno.map((a) => (
                  <li key={a.id}>
                    <RigaAllenamento allenamento={a} segno={segnoDi(a, oggi)} ss={ss} />
                  </li>
                ))}
              </ul>
            )}
            {gestisce && nellaStagione(scelto, stagione) && (
              <Button
                variant="outline"
                className="self-start"
                onClick={() => onNuovo({ data: scelto, ora: oraPiuUsata(tutti) })}
              >
                <PlusIcon data-icon="inline-start" />
                Nuovo in questo giorno
              </Button>
            )}
          </>
        ),
      })}
    />
  );
}
